/**
 * The Work roles. Compare a role against these (`role === WorkRole.TECHNICIAN`)
 * rather than repeating the code as a string.
 */
export const WorkRole = {
	REQUESTER: "REQUESTER",
	OPERATOR: "OPERATOR",
	TECHNICIAN: "TECHNICIAN",
	ADMINISTRATOR: "ADMINISTRATOR",
} as const;

export type WorkRoleCode = (typeof WorkRole)[keyof typeof WorkRole];
export type WorkModule =
	| "HOME"
	| "REQUESTS"
	| "WORK_ORDERS"
	| "ASSETS"
	| "TEAMS_USERS"
	| "DOCUMENTS"
	| "PROCEDURES"
	| "SETTINGS";
export type WorkCapability =
	| "VIEW_HOME"
	| "VIEW_REQUESTS"
	| "CREATE_REQUEST"
	| "EDIT_OWN_REQUEST"
	| "EDIT_ANY_REQUEST"
	| "CANCEL_OWN_REQUEST"
	| "REVIEW_REQUESTS"
	| "DELETE_REQUESTS"
	| "VIEW_WORK_ORDERS"
	| "CREATE_WORK_ORDER"
	| "EDIT_WORK_ORDER"
	| "CLAIM_WORK_ORDER"
	| "ASSIGN_WORK_ORDER"
	| "TRANSITION_WORK_ORDER"
	| "COMPLETE_WORK_ORDER"
	| "CANCEL_WORK_ORDER"
	| "DELETE_WORK_ORDER"
	| "RESTORE_WORK_ORDER"
	| "VIEW_ASSETS"
	| "CREATE_ASSET"
	| "EDIT_ASSET"
	| "DELETE_ASSET"
	| "VIEW_TEAMS_USERS"
	| "MANAGE_TEAMS_USERS"
	| "VIEW_DOCUMENTS"
	| "VIEW_PROCEDURES"
	| "CREATE_PROCEDURE"
	| "EDIT_PROCEDURE"
	| "MANAGE_SETTINGS";

export type WorkAccessProfile = {
	role: WorkRoleCode;
};

// Canonical page and action permissions are defined in Users, Teams & Access:
// https://app.notion.com/p/Users-Teams-Access-3cd7eb69ae9d80e481ebd7ca923ae650
// That MVP matrix covers Requester, Technician, and Administrator.
// Operator is not included in the source matrix.
// Administrators review and may edit pending requests, but do not submit or
// cancel them. Requesters may edit only requests they submitted.
export const WORK_ROLE_CAPABILITIES: Readonly<
	Record<WorkRoleCode, readonly WorkCapability[]>
> = {
	REQUESTER: [
		"VIEW_REQUESTS",
		"CREATE_REQUEST",
		"EDIT_OWN_REQUEST",
		"CANCEL_OWN_REQUEST",
	],
	OPERATOR: [
		"VIEW_REQUESTS",
		"CREATE_REQUEST",
		"VIEW_ASSETS",
		"VIEW_DOCUMENTS",
	],
	TECHNICIAN: [
		"VIEW_WORK_ORDERS",
		"CREATE_WORK_ORDER",
		"VIEW_ASSETS",
		"VIEW_DOCUMENTS",
		"VIEW_PROCEDURES",
		"CREATE_PROCEDURE",
		"EDIT_PROCEDURE",
		"COMPLETE_WORK_ORDER",
		"ASSIGN_WORK_ORDER",
		"CLAIM_WORK_ORDER",
	],
	ADMINISTRATOR: [
		"VIEW_REQUESTS",
		"EDIT_ANY_REQUEST",
		"REVIEW_REQUESTS",
		"DELETE_REQUESTS",
		"VIEW_WORK_ORDERS",
		"CREATE_WORK_ORDER",
		"EDIT_WORK_ORDER",
		"ASSIGN_WORK_ORDER",
		"CLAIM_WORK_ORDER",
		"TRANSITION_WORK_ORDER",
		"COMPLETE_WORK_ORDER",
		"CANCEL_WORK_ORDER",
		"DELETE_WORK_ORDER",
		"RESTORE_WORK_ORDER",
		"VIEW_ASSETS",
		"CREATE_ASSET",
		"EDIT_ASSET",
		"DELETE_ASSET",
		"VIEW_TEAMS_USERS",
		"MANAGE_TEAMS_USERS",
		"VIEW_DOCUMENTS",
		"VIEW_PROCEDURES",
		"CREATE_PROCEDURE",
		"EDIT_PROCEDURE",
		"MANAGE_SETTINGS",
	],
};

export const WORK_MODULE_CAPABILITIES: Readonly<
	Record<WorkModule, WorkCapability>
> = {
	HOME: "VIEW_HOME",
	REQUESTS: "VIEW_REQUESTS",
	WORK_ORDERS: "VIEW_WORK_ORDERS",
	ASSETS: "VIEW_ASSETS",
	TEAMS_USERS: "VIEW_TEAMS_USERS",
	DOCUMENTS: "VIEW_DOCUMENTS",
	PROCEDURES: "VIEW_PROCEDURES",
	SETTINGS: "MANAGE_SETTINGS",
};

export function canPerformAction(
	profile: WorkAccessProfile,
	capability: WorkCapability,
): boolean {
	return WORK_ROLE_CAPABILITIES[profile.role].includes(capability);
}

export function canAccessModule(
	profile: WorkAccessProfile,
	module: WorkModule,
): boolean {
	return canPerformAction(profile, WORK_MODULE_CAPABILITIES[module]);
}
